import { useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { useAuth } from "../context/auth-context";
import tokenService from "../services/token-service";
import { Wrench, ShoppingBag, Megaphone } from "lucide-react";

const REGISTER_ROLES = [
  { key: "handyman", label: "Become a Handyman", route: "/handyman-onboarding", icon: Wrench, iconColor: "text-purple-600" },
  { key: "vendor", label: "Become a Vendor", route: "/vendor-onboarding", icon: ShoppingBag, iconColor: "text-red-700" },
  { key: "marketing_executive", label: "Become Marketing Executive", route: null, icon: Megaphone, iconColor: "text-pink-600" },
];

const ROLE_ROUTES = {
  customer: "/customer/dashboard",
  handyman: "/handyman/dashboard",
  vendor: "/vendor/dashboard",
  marketing_executive: "/emp/dashboard",
  candidate: "/emp/dashboard",
};

export const useProfileMenu = (defaultRole = "customer") => {
  const { logout, user, login } = useAuth();
  const [openMenu, setOpenMenu] = useState(false);
  const [switchingRole, setSwitchingRole] = useState(null);
  const [meJobLoading, setMeJobLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const token = tokenService.getToken();

  // ── Display name ──────────────────────────────────────────────
  const displayName = useMemo(() => {
    const maybe =
      user?.fullName ||
      user?.name ||
      `${user?.firstName || ""} ${user?.lastName || ""}`.trim();
    return maybe || "Account";
  }, [user]);

  // ── Active role ───────────────────────────────────────────────
  const activeRole = useMemo(() => {
    if (location.pathname.startsWith("/handyman")) return "handyman";
    if (location.pathname.startsWith("/customer")) return "customer";
    if (location.pathname.startsWith("/vendor")) return "vendor";
    if (location.pathname.startsWith("/emp")) return "marketing_executive";
    if (location.pathname.startsWith("/dashboard") || location.pathname.startsWith("/me")) return "candidate";

    if (defaultRole) return defaultRole.toLowerCase();

    let r = user?.activeRole || user?.role?.name;
    if (typeof r === "object" && r?.name) r = r.name;
    if (typeof r === "string") return r.toLowerCase();
    return "customer";
  }, [user, defaultRole, location.pathname]);

  // ── Available roles ───────────────────────────────────────────
  const availableRoles = useMemo(() => {
    const raw = user?.availableRoles;
    let rolesList = [];
    if (Array.isArray(raw) && raw.length > 0 && typeof raw[0] === "string") {
      rolesList = raw.map((r) => r.toLowerCase());
    } else {
      const fromRoles = (user?.roles || [])
        .filter((r) => r.isActive && r.roleId?.name)
        .map((r) => r.roleId.name.toLowerCase());
      rolesList = fromRoles.length > 0 ? fromRoles : [activeRole];
    }
    
    // Ensure the defaultRole/activeRole is always included (for candidate/employee layouts)
    if (defaultRole === "candidate" && !rolesList.includes("candidate")) {
      rolesList = [...rolesList, "candidate"];
    }

    // Ensure "handyman" is included if onboarding is completed/submitted or if defaultRole/activeRole is handyman
    const hasHandymanApp =
      user?.handyman?.isSubmitted ||
      (user?.handymanApplicationStatus && user.handymanApplicationStatus !== "none") ||
      (typeof window !== "undefined" && localStorage.getItem("onboardingCompleted") === "true");

    if ((hasHandymanApp || defaultRole === "handyman" || activeRole === "handyman") && !rolesList.includes("handyman")) {
      rolesList = [...rolesList, "handyman"];
    }
    
    // Ensure "customer" is always included as every user can be a customer
    if (!rolesList.includes("customer")) {
      rolesList = ["customer", ...rolesList];
    }

    return rolesList;
  }, [user, activeRole, defaultRole]);

  // ── Application statuses ──────────────────────────────────────
  const meAppStatus = user?.meApplicationStatus || "none";
  const hmAppStatus = user?.handymanApplicationStatus || "none";

  // ── Roles to register ─────────────────────────────────────────
  const registrableRoles = useMemo(
    () =>
      REGISTER_ROLES.filter((r) => {
        if (availableRoles.includes(r.key)) return false;
        if (r.key === "marketing_executive" && (availableRoles.includes("candidate") || availableRoles.includes("marketing_executive"))) return false;
        if (r.key === "handyman" && hmAppStatus === "approved") return false;
        if (r.key === "marketing_executive" && meAppStatus === "approved") return false;
        return true;
      }),
    [availableRoles, hmAppStatus, meAppStatus]
  );

  // ── Logout ────────────────────────────────────────────────────
  const handleLogout = async () => {
    try {
      const currentToken = tokenService.getToken() || token;
      await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/logout`,
        {},
        { headers: { Authorization: `Bearer ${currentToken}` } }
      );
    } catch (error) {
      console.error("Logout API call failed", error);
    } finally {
      logout();
      navigate("/login");
    }
  };

  // ── Become Marketing Executive — find ME job then navigate ───
  const handleBecomeMarketing = async () => {
    setOpenMenu(false);
    setMeJobLoading(true);
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/jobs?title=marketing+executive&limit=1`
      );
      const meJob = data?.data?.jobs?.[0];
      if (meJob?._id) {
        navigate(`/job-details/apply/${meJob._id}`);
      } else {
        navigate("/career");
      }
    } catch (error) {
      console.error("Find ME job failed", error);
      navigate("/career");
    } finally {
      setMeJobLoading(false);
    }
  };

  // ── Switch role ───────────────────────────────────────────────
  const handleSwitchRole = async (roleName) => {
    if (roleName === activeRole) {
      setOpenMenu(false);
      return;
    }
    setSwitchingRole(roleName);
    setOpenMenu(false);
    try {
      const currentToken = tokenService.getToken() || token;
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}/switch-role`,
        { roleName },
        { headers: { Authorization: `Bearer ${currentToken}` } }
      );
      const newToken = data.data?.token;
      const newUser = data.data?.user;
      if (newToken && newUser) {
        login(newToken, newUser);
      }
      const targetRoute =
        roleName === "candidate" && (newUser?.meApplicationStatus === "approved" || user?.meApplicationStatus === "approved")
          ? "/emp/dashboard"
          : ROLE_ROUTES[roleName] || "/dashboard";
      navigate(targetRoute);
    } catch (err) {
      console.warn(`Role switch API notice for ${roleName}:`, err.response?.data?.error?.message || err.message);
      const targetRoute =
        roleName === "candidate" && user?.meApplicationStatus === "approved"
          ? "/emp/dashboard"
          : ROLE_ROUTES[roleName] || "/dashboard";
      navigate(targetRoute);
    } finally {
      setSwitchingRole(null);
    }
  };

  return {
    user,
    openMenu,
    setOpenMenu,
    switchingRole,
    meJobLoading,
    displayName,
    activeRole,
    availableRoles,
    registrableRoles,
    meAppStatus,
    hmAppStatus,
    handleLogout,
    handleBecomeMarketing,
    handleSwitchRole,
  };
};
